"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import CloseIcon from "../icons/CloseIcon";
import SearchIcon from "../icons/SearchIcon";
import FiltersIcon from "../icons/FiltersIcon";
import Button from "./Button";
import Checkbox from "./Checkbox";
import ModalPortal from "./ModalPortal";
import { useScrollLock } from "@/lib/use-scroll-lock";

type FilterOptions = {
  categories: string[];
  brands: string[];
  collections?: string[];
  subCategories?: string[];
  sizes?: string[];
  growingMethods?: string[];
  types?: string[];
  strains?: string[];
  dominantTerpenes?: string[];
  flavors?: string[];
  provincesOfOrigin?: string[];
  regionsOfProduct?: string[];
  extractionProcesses?: string[];
  carrierOils?: string[];
  priceMax?: number;
  thcMax?: number | null;
  cbdMax?: number | null;
  cbnMax?: number | null;
  cbcMax?: number | null;
  thcvMax?: number | null;
  piecesMax?: number | null;
  totalTerpeneMax?: number | null;
};

type CurrentFilters = {
  q?: string;
  categories?: string[];
  brands?: string[];
  collections?: string[];
  subCategories?: string[];
  sizes?: string[];
  growingMethods?: string[];
  types?: string[];
  strains?: string[];
  dominantTerpenes?: string[];
  flavors?: string[];
  provincesOfOrigin?: string[];
  regionsOfProduct?: string[];
  extractionProcesses?: string[];
  carrierOils?: string[];
  strainType?: string;
  onSale?: string;
  thcMin?: string;
  thcMax?: string;
  cbdMin?: string;
  cbdMax?: string;
  cbnMin?: string;
  cbnMax?: string;
  cbcMin?: string;
  cbcMax?: string;
  thcvMin?: string;
  thcvMax?: string;
  piecesMin?: string;
  piecesMax?: string;
  totalTerpeneMin?: string;
  totalTerpeneMax?: string;
  priceMin?: string;
  priceMax?: string;
};

type FilterValue = string | boolean | string[] | undefined;

type ProductFiltersProps = {
  options: FilterOptions;
  searchQuery?: string;
  currentFilters: CurrentFilters;
  onFilterChange: (filters: Record<string, FilterValue>) => void;
  onClearFilters: () => void;
  mode?: "desktop" | "mobile";
  onMobileModalOpenChange?: (open: boolean) => void;
};

type MultiSelectConfig = {
  type: "multi";
  id: keyof CurrentFilters & string;
  title: string;
  options: string[];
};

type RangeConfig = {
  type: "range";
  id: string;
  title: string;
  minKey: keyof CurrentFilters & string;
  maxKey: keyof CurrentFilters & string;
  max: number;
  step: number;
  formatLabel: (value: number) => string;
};

type FilterConfig = MultiSelectConfig | RangeConfig;

function toNumberOr(value: string | undefined, fallback: number) {
  const n = value === undefined || value === "" ? NaN : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function compactList(values: string[] | undefined): string[] {
  return Array.from(new Set((values || []).map((value) => value.trim()).filter(Boolean)));
}

function DualRange({
  min,
  max,
  step,
  valueMin,
  valueMax,
  onChange,
  formatLabel,
}: {
  min: number;
  max: number;
  step: number;
  valueMin: number;
  valueMax: number;
  onChange: (nextMin: number, nextMax: number) => void;
  formatLabel?: (v: number) => string;
}) {
  const safeMaxBound = Math.max(min + step, max);
  const safeMin = clamp(valueMin, min, safeMaxBound);
  const safeMax = clamp(valueMax, min, safeMaxBound);
  const pctMin = ((safeMin - min) / (safeMaxBound - min)) * 100;
  const pctMax = ((safeMax - min) / (safeMaxBound - min)) * 100;
  const badgesTooClose = pctMax - pctMin < 12;
  const minBadgeNudgePx = badgesTooClose ? -14 : 20;
  const maxBadgeNudgePx = badgesTooClose ? 14 : -20;

  return (
    <div className="w-full">
      <div className="relative mb-2 h-7">
        <span
          className="absolute top-0 inline-flex -translate-x-1/2 items-center rounded-lg border border-neutral-10 bg-bg-0 px-2 py-1 text-body-12-desktop font-medium text-text-primary"
          style={{
            left: `${pctMin}%`,
            transform: `translateX(calc(-50% + ${minBadgeNudgePx}px))`,
          }}
        >
          {formatLabel ? formatLabel(safeMin) : String(safeMin)}
        </span>
        <span
          className="absolute top-0 inline-flex -translate-x-1/2 items-center rounded-lg border border-neutral-10 bg-bg-0 px-2 py-1 text-body-12-desktop font-medium text-text-primary"
          style={{
            left: `${pctMax}%`,
            transform: `translateX(calc(-50% + ${maxBadgeNudgePx}px))`,
          }}
        >
          {formatLabel ? formatLabel(safeMax) : String(safeMax)}
        </span>
      </div>

      <div className="relative h-6">
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-blue-10" />
        <div
          className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-blue-60"
          style={{
            left: `${pctMin}%`,
            width: `${Math.max(0, pctMax - pctMin)}%`,
          }}
        />
        <input
          type="range"
          min={min}
          max={safeMaxBound}
          step={step}
          value={safeMin}
          onChange={(e) => {
            const next = Number(e.target.value);
            onChange(Math.min(next, safeMax), safeMax);
          }}
          className="pf-range pf-range--min absolute left-0 right-0 top-0 h-6 w-full appearance-none bg-transparent pointer-events-auto"
          style={{ WebkitAppearance: "none" }}
        />
        <input
          type="range"
          min={min}
          max={safeMaxBound}
          step={step}
          value={safeMax}
          onChange={(e) => {
            const next = Number(e.target.value);
            onChange(safeMin, Math.max(next, safeMin));
          }}
          className="pf-range pf-range--max absolute left-0 right-0 top-0 h-6 w-full appearance-none bg-transparent pointer-events-auto"
          style={{ WebkitAppearance: "none" }}
        />
      </div>
    </div>
  );
}

const filterTextActionClass =
  "text-body-14-desktop font-medium text-blue-80 transition-colors select-none hover:text-blue-100 active:text-blue-100 disabled:text-neutral-15 disabled:cursor-not-allowed disabled:pointer-events-none";

const filterChipButtonClass =
  "inline-flex items-center gap-1.5 rounded-xl border border-blue-10 bg-blue-5 px-3 py-2 text-body-14-desktop font-medium text-blue-50 transition-all select-none hover:border-blue-15 hover:bg-blue-10 active:scale-95";

const filterSectionToggleClass =
  "flex w-full items-center justify-between rounded-lg px-1 py-2 text-left text-text-primary transition-colors select-none hover:text-blue-80";

const filterShowMoreClass =
  "inline-flex items-center gap-2 text-body-16-desktop font-medium text-blue-80 transition-colors select-none hover:text-blue-100 active:text-blue-100";

const filterModalTriggerClass =
  "inline-flex items-center gap-2 rounded-xl border border-neutral-10 bg-constant-white px-4 py-2.5 text-body-16-mobile font-medium text-text-secondary transition-all select-none hover:border-blue-10 hover:bg-blue-5 hover:text-blue-80 active:scale-95";

export default function ProductFilters({
  options,
  searchQuery,
  currentFilters,
  onFilterChange,
  onClearFilters,
  mode = "desktop",
  onMobileModalOpenChange,
}: ProductFiltersProps) {
  const isMobile = mode === "mobile";
  const [searchValue, setSearchValue] = useState(searchQuery || "");
  const [pendingSearchValue, setPendingSearchValue] = useState(searchQuery || "");
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<CurrentFilters>({});
  const [open, setOpen] = useState<Record<string, boolean>>({
    specials: true,
    categories: true,
    subCategories: true,
    price: true,
    brands: true,
    thc: true,
    cbd: true,
    cbn: false,
    sizes: false,
    pieces: false,
    growingMethods: false,
    types: false,
    strains: false,
    dominantTerpenes: false,
    totalTerpene: false,
    flavors: false,
    provincesOfOrigin: false,
    regionsOfProduct: false,
    extractionProcesses: false,
    carrierOils: false,
    collections: false,
  });
  const [searchByGroup, setSearchByGroup] = useState<Record<string, string>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const commitRefs = useRef<Record<string, number | null>>({});

  useScrollLock(isMobileModalOpen);

  const filtersToUse = isMobile && isMobileModalOpen ? pendingFilters : currentFilters;
  const priceMaxBound = Math.max(10, Math.round(options.priceMax ?? 240));
  // Potency is a percentage of product weight, physically capped at 100%.
  // Source data occasionally carries mg/g values mislabelled as %, which would
  // otherwise surface as nonsense bounds (500%, 895%); clamp the slider max.
  const thcMaxBound = Math.min(100, Math.max(1, Math.round(options.thcMax ?? 100)));
  const cbdMaxBound = Math.min(100, Math.max(1, Math.round(options.cbdMax ?? 100)));

  const rangeConfigs = useMemo(
    () =>
      [
        {
          type: "range" as const,
          id: "price",
          title: "Price",
          minKey: "priceMin",
          maxKey: "priceMax",
          max: priceMaxBound,
          step: 1,
          formatLabel: (value: number) => `$${value}`,
        },
        {
          type: "range" as const,
          id: "thc",
          title: "THC Potency",
          minKey: "thcMin",
          maxKey: "thcMax",
          max: thcMaxBound,
          step: 1,
          formatLabel: (value: number) => `${value}%`,
        },
        {
          type: "range" as const,
          id: "cbd",
          title: "CBD Potency",
          minKey: "cbdMin",
          maxKey: "cbdMax",
          max: cbdMaxBound,
          step: 1,
          formatLabel: (value: number) => `${value}%`,
        },
        options.cbnMax
          ? {
            type: "range" as const,
            id: "cbn",
            title: "CBN Potency",
            minKey: "cbnMin",
            maxKey: "cbnMax",
            max: Math.min(100, Math.max(1, Math.round(options.cbnMax))),
            step: 1,
            formatLabel: (value: number) => `${value}%`,
          }
          : null,
        options.cbcMax
          ? {
            type: "range" as const,
            id: "cbc",
            title: "CBC Potency",
            minKey: "cbcMin",
            maxKey: "cbcMax",
            max: Math.min(100, Math.max(1, Math.round(options.cbcMax))),
            step: 1,
            formatLabel: (value: number) => `${value}%`,
          }
          : null,
        options.thcvMax
          ? {
            type: "range" as const,
            id: "thcv",
            title: "THCV Potency",
            minKey: "thcvMin",
            maxKey: "thcvMax",
            max: Math.min(100, Math.max(1, Math.round(options.thcvMax))),
            step: 1,
            formatLabel: (value: number) => `${value}%`,
          }
          : null,
        options.piecesMax
          ? {
            type: "range" as const,
            id: "pieces",
            title: "Pieces",
            minKey: "piecesMin",
            maxKey: "piecesMax",
            max: Math.max(1, Math.round(options.piecesMax)),
            step: 1,
            formatLabel: (value: number) => String(value),
          }
          : null,
        options.totalTerpeneMax
          ? {
            type: "range" as const,
            id: "totalTerpene",
            title: "Total Terpene %",
            minKey: "totalTerpeneMin",
            maxKey: "totalTerpeneMax",
            max: Math.min(100, Math.max(1, Math.ceil(options.totalTerpeneMax))),
            step: 0.1,
            formatLabel: (value: number) => `${value}%`,
          }
          : null,
      ].filter(Boolean) as RangeConfig[],
    [
      cbdMaxBound,
      options.cbnMax,
      options.cbcMax,
      options.piecesMax,
      options.thcvMax,
      options.totalTerpeneMax,
      priceMaxBound,
      thcMaxBound,
    ]
  );

  const filterConfigs = useMemo<FilterConfig[]>(() => {
    const multiConfigs = [
      { type: "multi", id: "categories", title: "Product Category", options: compactList(options.categories) },
      { type: "multi", id: "subCategories", title: "SubCategory", options: compactList(options.subCategories) },
      { type: "multi", id: "brands", title: "Brands", options: compactList(options.brands) },
      { type: "multi", id: "sizes", title: "Size", options: compactList(options.sizes) },
      { type: "multi", id: "growingMethods", title: "Growing Method", options: compactList(options.growingMethods) },
      { type: "multi", id: "types", title: "Type", options: compactList(options.types) },
      { type: "multi", id: "strains", title: "Strain", options: compactList(options.strains) },
      { type: "multi", id: "dominantTerpenes", title: "Dominant Terpene", options: compactList(options.dominantTerpenes) },
      { type: "multi", id: "flavors", title: "Flavors", options: compactList(options.flavors) },
      { type: "multi", id: "provincesOfOrigin", title: "Province of Origin", options: compactList(options.provincesOfOrigin) },
      { type: "multi", id: "regionsOfProduct", title: "Region Of Product", options: compactList(options.regionsOfProduct) },
      { type: "multi", id: "extractionProcesses", title: "Extraction Process", options: compactList(options.extractionProcesses) },
      { type: "multi", id: "carrierOils", title: "Carrier Oil", options: compactList(options.carrierOils) },
      { type: "multi", id: "collections", title: "Collections", options: compactList(options.collections) },
    ].filter((config) => config.options.length > 0) as MultiSelectConfig[];

    return [
      ...multiConfigs.filter((config) => config.id === "categories" || config.id === "subCategories"),
      ...rangeConfigs.filter((config) => config.id === "price"),
      ...multiConfigs.filter((config) => config.id === "brands"),
      ...rangeConfigs.filter((config) =>
        ["thc", "cbd", "cbn", "cbc", "thcv"].includes(config.id)
      ),
      ...multiConfigs.filter((config) => config.id === "sizes"),
      ...rangeConfigs.filter((config) => config.id === "pieces"),
      ...multiConfigs.filter(
        (config) =>
          config.id !== "categories" &&
          config.id !== "subCategories" &&
          config.id !== "brands" &&
          config.id !== "sizes" &&
          config.id !== "collections"
      ),
      ...rangeConfigs.filter((config) => config.id === "totalTerpene"),
      ...multiConfigs.filter((config) => config.id === "collections"),
    ];
  }, [options, rangeConfigs]);

  const updateFilter = (updates: CurrentFilters) => {
    if (isMobile && isMobileModalOpen) {
      setPendingFilters((prev) => ({ ...prev, ...updates }));
      return;
    }
    onFilterChange(updates as Record<string, FilterValue>);
  };

  const removeMultiValue = (key: keyof CurrentFilters & string, value: string) => {
    const prev = Array.isArray(filtersToUse[key]) ? (filtersToUse[key] as string[]) : [];
    const next = prev.filter((item) => item !== value);
    updateFilter({ [key]: next.length ? next : undefined } as CurrentFilters);
  };

  const clearRange = (config: RangeConfig) => {
    updateFilter({ [config.minKey]: undefined, [config.maxKey]: undefined } as CurrentFilters);
  };

  const activeChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; value: string; config?: RangeConfig }> = [];
    for (const config of filterConfigs) {
      if (config.type === "multi") {
        const selected = Array.isArray(filtersToUse[config.id]) ? (filtersToUse[config.id] as string[]) : [];
        for (const value of selected) chips.push({ key: config.id, label: value, value });
      } else if (config.type === "range") {
        const min = typeof filtersToUse[config.minKey] === "string" ? String(filtersToUse[config.minKey]) : "";
        const max = typeof filtersToUse[config.maxKey] === "string" ? String(filtersToUse[config.maxKey]) : "";
        if (min || max) {
          chips.push({
            key: config.id,
            label: `${config.title} ${min ? config.formatLabel(Number(min)) : config.formatLabel(0)}-${max ? config.formatLabel(Number(max)) : config.formatLabel(config.max)}`,
            value: config.id,
            config,
          });
        }
      }
    }

    if (filtersToUse.strainType) {
      chips.push({ key: "strainType", label: filtersToUse.strainType, value: filtersToUse.strainType });
    }
    if (filtersToUse.onSale === "true") {
      chips.push({ key: "onSale", label: "On Sale", value: "true" });
    }
    return chips;
  }, [filterConfigs, filtersToUse]);

  const activeCount = activeChips.length;

  const pendingFilterCount = useMemo(() => {
    if (!isMobile || !isMobileModalOpen) return 0;
    return activeChips.length;
  }, [activeChips.length, isMobile, isMobileModalOpen]);

  useEffect(() => {
    if (isMobileModalOpen) {
      setPendingFilters({ ...currentFilters });
      setPendingSearchValue(currentFilters.q || searchQuery || "");
    }
  }, [isMobileModalOpen, currentFilters, searchQuery]);

  useEffect(() => {
    onMobileModalOpenChange?.(isMobileModalOpen);
  }, [isMobileModalOpen, onMobileModalOpenChange]);

  useEffect(() => {
    setSearchValue(searchQuery || "");
    setPendingSearchValue(searchQuery || "");
  }, [searchQuery]);

  useEffect(() => {
    if (isMobile && isMobileModalOpen) return;
    const t = window.setTimeout(() => {
      onFilterChange({ q: searchValue.trim() || undefined });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, isMobile, isMobileModalOpen]);

  useEffect(() => {
    return () => {
      for (const timeout of Object.values(commitRefs.current)) {
        if (timeout) window.clearTimeout(timeout);
      }
    };
  }, []);

  const handleMobileApply = () => {
    onFilterChange({
      ...(pendingFilters as Record<string, FilterValue>),
      q: pendingSearchValue.trim() || undefined,
    });
    setIsMobileModalOpen(false);
  };

  const handleMobileClear = () => {
    setPendingFilters({});
    onClearFilters();
    setIsMobileModalOpen(false);
  };

  const scheduleRangeCommit = (config: RangeConfig, minValue: number, maxValue: number) => {
    if (commitRefs.current[config.id]) window.clearTimeout(commitRefs.current[config.id]!);
    commitRefs.current[config.id] = window.setTimeout(() => {
      updateFilter({
        [config.minKey]: minValue > 0 ? String(minValue) : undefined,
        [config.maxKey]: maxValue < config.max ? String(maxValue) : undefined,
      } as CurrentFilters);
    }, 250);
  };

  const renderMultiSelect = (config: MultiSelectConfig) => {
    const selected = Array.isArray(filtersToUse[config.id]) ? (filtersToUse[config.id] as string[]) : [];
    const query = (searchByGroup[config.id] || "").trim().toLowerCase();
    const filtered = query
      ? config.options.filter((option) => option.toLowerCase().includes(query))
      : config.options;
    const expanded = Boolean(expandedGroups[config.id]);
    const visible = expanded ? filtered : filtered.slice(0, 9);
    const listClass =
      expanded && filtered.length > 9
        ? "mt-4 max-h-[min(45vh,240px)] overflow-y-auto overscroll-y-contain pr-1 space-y-3 [scrollbar-gutter:stable]"
        : "mt-4 space-y-3 ml-1";

    return (
      <div className="mt-3">
        <div className="relative">
          <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
            <SearchIcon size={20} color="currentColor" />
          </div>
          <input
            value={searchByGroup[config.id] || ""}
            onChange={(e) => setSearchByGroup((prev) => ({ ...prev, [config.id]: e.target.value }))}
            placeholder="Search"
            className="w-full rounded-xl border border-neutral-10 bg-constant-white py-2 pl-10 pr-2 text-body-16-desktop text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-blue-15"
          />
        </div>
        <div className={listClass}>
          {visible.map((option) => {
            const checked = selected.includes(option);
            return (
              <label key={option} className="flex cursor-pointer items-center gap-3 text-body-16 font-medium text-text-primary">
                <Checkbox
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((value) => value !== option)
                      : [...selected, option];
                    updateFilter({ [config.id]: next.length ? next : undefined } as CurrentFilters);
                  }}
                  boxSize={22}
                />
                <span className="truncate">{option}</span>
              </label>
            );
          })}
        </div>
        {filtered.length > 9 && (
          <Button raw
            type="button"
            onClick={() => setExpandedGroups((prev) => ({ ...prev, [config.id]: !expanded }))}
            className={`mt-4 ${filterShowMoreClass}`}
          >
            {expanded ? "Show less" : "Show more"}
            <ChevronDownIcon
              size={16}
              color="currentColor"
              className={expanded ? "rotate-180 transition-transform" : "transition-transform"}
            />
          </Button>
        )}
      </div>
    );
  };

  const renderRange = (config: RangeConfig) => {
    const minValue = toNumberOr(filtersToUse[config.minKey] as string | undefined, 0);
    const maxValue = toNumberOr(filtersToUse[config.maxKey] as string | undefined, config.max);

    return (
      <div className="mt-3">
        <DualRange
          min={0}
          max={config.max}
          step={config.step}
          valueMin={minValue}
          valueMax={maxValue}
          onChange={(mn, mx) => scheduleRangeCommit(config, mn, mx)}
          formatLabel={config.formatLabel}
        />
      </div>
    );
  };

  const renderSpecialsSection = () => {
    const selected = filtersToUse.onSale === "true";
    return (
      <div key="specials" className="border-t border-neutral-10 pt-3 pb-3">
        <Button raw
          type="button"
          onClick={() => setOpen((state) => ({ ...state, specials: !state.specials }))}
          className={filterSectionToggleClass}
        >
          <span className="text-body-18 font-bold text-text-primary">Specials</span>
          {open.specials ? (
            <ChevronUpIcon size={18} color="currentColor" />
          ) : (
            <ChevronDownIcon size={18} color="currentColor" />
          )}
        </Button>
        {open.specials && (
          <div className="mt-3 flex flex-wrap gap-[6px]">
            <Button raw
              type="button"
              onClick={() => updateFilter({ onSale: selected ? undefined : "true" })}
              className={`rounded-xl border px-3 py-2 text-body-16-desktop font-medium transition-colors ${selected
                  ? "border-neutral-70 bg-neutral-70 text-constant-white"
                  : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10 hover:bg-blue-5 hover:text-blue-80"
                }`}
            >
              On Sale
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderSection = (config: FilterConfig) => (
    <div key={config.id} className="border-t border-neutral-10 pt-3 pb-3">
      <Button raw
        type="button"
        onClick={() => setOpen((state) => ({ ...state, [config.id]: !state[config.id] }))}
        className={filterSectionToggleClass}
      >
        <span className="text-body-18 font-bold text-text-primary">{config.title}</span>
        {open[config.id] ? (
          <ChevronUpIcon size={18} color="currentColor" />
        ) : (
          <ChevronDownIcon size={18} color="currentColor" />
        )}
      </Button>
      {open[config.id] && (
        <>
          {config.type === "multi" && renderMultiSelect(config)}
          {config.type === "range" && renderRange(config)}
          {config.id === "strains" && (
            <div className="mt-3 flex flex-wrap gap-[6px]">
              {["Indica", "Hybrid", "Sativa"].map((strainType) => {
                const selected = filtersToUse.strainType?.toLowerCase() === strainType.toLowerCase();
                return (
                  <Button raw
                    type="button"
                    key={strainType}
                    onClick={() => updateFilter({ strainType: selected ? undefined : strainType })}
                    className={`rounded-xl border px-3 py-2 text-body-16-desktop font-medium transition-colors ${selected
                        ? "border-neutral-70 bg-neutral-70 text-constant-white"
                        : "border-neutral-10 bg-constant-white text-text-secondary hover:border-blue-10 hover:bg-blue-5 hover:text-blue-80"
                      }`}
                  >
                    {strainType}
                  </Button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  const activeChipBlock = activeCount > 0 && (
    <div className="mb-4 flex flex-wrap gap-2">
      {activeChips.map((chip) => (
        <Button raw
          type="button"
          key={`${chip.key}:${chip.label}`}
          onClick={() => {
            if (chip.config) clearRange(chip.config);
            else if (chip.key === "strainType") updateFilter({ strainType: undefined });
            else if (chip.key === "onSale") updateFilter({ onSale: undefined });
            else removeMultiValue(chip.key as keyof CurrentFilters & string, chip.value);
          }}
          className={filterChipButtonClass}
        >
          <span className="truncate">{chip.label}</span>
          <CloseIcon size={16} color="currentColor" />
        </Button>
      ))}
    </div>
  );

  const panel = (
    <div>
      <div className="relative">
        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
          <SearchIcon size={20} color="currentColor" />
        </div>
        <input
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search"
          className="w-full rounded-xl border border-neutral-10 bg-constant-white py-2 pl-10 pr-2 text-body-16-desktop text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-blue-15"
        />
      </div>

      <div className="flex items-center justify-between mt-5">
        <div className="flex items-center gap-2">
          <div className="text-body-18 font-medium text-text-primary">Filters</div>
          {activeCount > 0 && (
            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-neutral-70 px-2 text-body-14-desktop font-medium text-constant-white">
              {activeCount}
            </span>
          )}
        </div>
        <Button raw
          type="button"
          onClick={onClearFilters}
          disabled={activeCount === 0}
          className={filterTextActionClass}
        >
          Clear all
        </Button>
      </div>

      <div className="mt-4">{activeChipBlock}</div>
      <div className="space-y-1">{renderSpecialsSection()}{filterConfigs.map(renderSection)}</div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Button raw type="button" onClick={() => setIsMobileModalOpen(true)} className={filterModalTriggerClass}>
          <FiltersIcon size={16} color="currentColor" />
          <span>Filters</span>
          {activeCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-80 px-1.5 text-body-12-mobile font-medium text-constant-white">
              {activeCount}
            </span>
          )}
        </Button>

        {isMobileModalOpen && (
          <ModalPortal>
          <div className="fixed inset-0 z-[1000] flex flex-col bg-constant-white">

            <div className="flex-1 overflow-y-auto pb-24">
              <div className="p-4">
                <div className="relative mb-4">
                  <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <SearchIcon size={20} color="currentColor" />
                  </div>
                  <input
                    value={pendingSearchValue}
                    onChange={(e) => setPendingSearchValue(e.target.value)}
                    placeholder="Search"
                    className="w-full rounded-xl border border-neutral-10 bg-constant-white py-2 pl-10 pr-2 text-body-16-desktop text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-blue-15"
                  />
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="text-body-18 font-medium text-text-primary">Filters</div>
                  <Button raw
                    type="button"
                    onClick={handleMobileClear}
                    disabled={activeCount === 0}
                    className={filterTextActionClass}
                  >
                    Clear all
                  </Button>
                </div>

                {activeChipBlock}
                <div className="space-y-3">{renderSpecialsSection()}{filterConfigs.map(renderSection)}</div>
              </div>
            </div>

            <div className="sticky bottom-0 left-0 right-0 border-t border-neutral-10 bg-constant-white shadow-lg">
              <div className="p-4 pb-6" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}>
                {pendingFilterCount > 0 ? (
                  <Button variant="solid" color="primary" size="lg" className="w-full rounded-xl" onClick={handleMobileApply}>
                    Apply
                  </Button>
                ) : (
                  <Button
                    variant="bordered"
                    color="white"
                    size="lg"
                    className="w-full rounded-xl"
                    onClick={() => setIsMobileModalOpen(false)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </div>
          </div>
          </ModalPortal>
        )}
      </>
    );
  }

  return <div className="mb-4">{panel}</div>;
}
